import { useApi } from '@backstage/core-plugin-api';
import { uptimeMonitorApiRef } from '../api/UptimeMonitorApi';
import { useCallback, useEffect, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { TransactionGroupsRequest, TransactionGroupsListResponse, PerformanceSortOption } from '../types';

export const useTransactionGroups = (params: TransactionGroupsRequest & { projectId?: string }) => {
    const api = useApi(uptimeMonitorApiRef);
    const [cursor, setCursor] = useState<string | undefined>(undefined);
  
    const [queryParams, setQueryParams] = useState<Record<string, string>>({
      sort: PerformanceSortOption.NEWEST,
      limit: '10',
    });
  
    const getTransactionGroups = useCallback(async (): Promise<TransactionGroupsListResponse> => {
      try {
        setTimeout(() => (document.activeElement as HTMLElement).blur(), 0);
  
        const currentParams = {
          ...params,
          query: {
            ...queryParams,
            cursor: cursor,
            ...(params.projectId && { project: params.projectId }),
          },
        };
  
        const result = await api.getTransactionGroups(currentParams);
        return result;
      } catch (err: any) {
        console.error('Error fetching transaction groups, returning empty data:', err);
        return {
          transactionGroups: [],
          pagination: {
            next: undefined,
            previous: undefined,
          },
        };
      }
    }, [api, params, cursor, queryParams]);
  
    const [state, fetch] = useAsyncFn(
      () => getTransactionGroups(),
      [params.host, params.org, params.projectId, cursor, queryParams],
    );
  
    useEffect(() => {
      fetch();
    }, [params.host, params.org, params.projectId, cursor, queryParams, fetch]);
  
    const handlePageChange = useCallback((newCursor: string | undefined) => {
      setCursor(newCursor);
    }, []);
  
    const handleFilterChange = useCallback(
      (newFilters: Record<string, string>) => {
        setQueryParams(prev => ({ ...prev, ...newFilters }));
        setCursor(undefined);
      },
      [],
    );
  
    const handleSearch = useCallback((searchQuery: string) => {
      setQueryParams(prev => ({ ...prev, query: searchQuery }));
      setCursor(undefined);
    }, []);
  
    const handleDateRangeChange = useCallback((start: string | undefined, end: string | undefined) => {
      setQueryParams(prev => {
        const newParams = { ...prev };
        
        if (start !== undefined) {
          newParams.start = start;
        } else {
          delete newParams.start;
        }
        
        if (end !== undefined) {
          newParams.end = end;
        } else {
          delete newParams.end;
        }
        
        return newParams;
      });
      setCursor(undefined);
    }, []);
  
    const refetch = useCallback(() => {
      fetch();
    }, [fetch]);
  
    return {
      isLoading: state.loading,
      data: state?.value?.transactionGroups || [],
      pagination: state?.value?.pagination || { next: undefined, previous: undefined },
      error: undefined,
      handlePageChange,
      handleFilterChange,
      handleSearch,
      handleDateRangeChange,
      refetch,
      currentFilters: queryParams,
    };
  };